<template>
  <div class="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto">
    <PageHeader
      title="Historia zamówień"
      subtitle="Tu sprawdzisz historię swoich zamówień i pobierzesz faktury"
    />

    <!-- Orders Table -->
    <div class="bg-white border border-neutral-100 rounded-lg-custom overflow-hidden overflow-x-auto">
      <!-- Table Header -->
      <div
        class="grid grid-cols-[minmax(150px,auto)_minmax(150px,auto)_1fr_minmax(80px,auto)_minmax(120px,auto)_minmax(140px,auto)] border-b border-neutral-100 h-14 items-center min-w-[800px]"
      >
        <div class="px-6 text-sm font-bold text-neutral-450 w-60">Numer zamówienia</div>
        <div class="px-6 text-sm font-bold text-neutral-450">Kurs</div>
        <div class="px-6 text-sm font-bold text-neutral-450 text-right">Kwota</div>
        <div class="px-6 text-sm font-bold text-neutral-450 text-center">Status</div>
        <div class="px-6 text-sm font-bold text-neutral-450 text-center">Faktura</div>
        <!-- <div class="px-6 text-sm font-bold text-neutral-450 text-center">Umowa</div> -->
      </div>

      <!-- Table Body -->
      <div class="divide-y divide-neutral-75 ">
        <div
          v-for="(order, index) in orders"
          :key="order.id"
          class="grid grid-cols-[minmax(150px,auto)_minmax(150px,auto)_1fr_minmax(80px,auto)_minmax(120px,auto)_minmax(140px,auto)]  items-center min-w-[800px] py-2"
          :class="{ 'bg-neutral-50': index % 2 === 1 }"
        >
          <!-- Order Number -->
          <div class="w-60 px-6">
            <span class="text-sm font-bold text-black">{{ order.orderNumber }}</span><br />
            <span class="text-sm text-neutral-450">{{ order.date }}</span>
          </div>


          <!-- Course -->
            <div class=" px-6 flex items-center gap-4">
            <!-- <div v-if="order.level?.length" class="flex flex-wrap gap-2 items-start">
              <PathTag
              v-for="tag in order.level"
              :key="tag.id"
              :tag="tag"
              :width=80
              :height=100
              />
            </div> -->

              <div class="flex-1">
              <span class="text-sm font-semibold text-black">{{ order.title }}</span>
              <div class="text-xs text-neutral-600 mt-1">{{ order.terms }}</div>
              <div v-if="order.participants" class="text-xs text-neutral-600 mt-1">{{ order.participants }}</div>
            </div>
          
            </div>

          <!-- Amount -->
          <div class="px-6 text-right whitespace-nowrap">
            <span class="text-sm text-black">{{ order.amount }} zł</span>
          </div>

          <!-- Status -->
          <div class="px-6 flex justify-center w-20">
            <span
              class="inline-flex items-center px-3 py-1 rounded-full text-xs"
              :class="getStatusClass(order.status)"
            >
              {{ getStatusLabel(order.status) }}
            </span>
          </div>

          <!-- Invoice -->
          <div class="px-6 flex justify-center">
            <a
              v-if="order.invoiceUrl"
              :href="order.invoiceUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-2 text-sm text-black hover:text-primary-500 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
              </svg>
              Pobierz
            </a>
            <span v-else class="text-sm text-neutral-450">—</span>
          </div>

           <!-- Umowa
          <div class="px-6 flex justify-center">
            <a
              v-if="order.contractUrl"
              :href="order.contractUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-2 text-sm text-black hover:text-primary-500 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
              </svg>
              Pobierz
            </a>
            <span v-else class="text-sm text-neutral-450">—</span>
          </div> -->
        </div>

        <div v-if="!loading && orders.length === 0" class="px-6 py-10 text-sm text-neutral-450">
          Brak zamówień.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PathTag from '../../components/PathTag.vue';
import PageHeader from '../../components/PageHeader.vue';
import Tag from '@shared/model/Tag.js';
import rpAPI from '@shared/service/rpAPI.js';

export default {
  name: 'OrdersPage',
  components: {
    PageHeader,
    PathTag,
  },
  data() {
    return {
      rpAPI,
      loading: true,
      orders: [],
    };
  },
  async mounted() {
    try {
      this.loading = true;
      await rpAPI.ensureUser();
      const rawOrders = await rpAPI.getOrders();
      console.log("Fetched orders", rawOrders[0]);
      this.orders = (rawOrders || []).map(this.mapOrder);
    } catch (error) {
      this.orders = [];
      if (this.$notify?.error) {
        this.$notify.error('Nie udało się pobrać zamówień.');
      }
    } finally {
      this.loading = false;
    }
  },
  methods: {
    mapOrder(order) {
        //console.log("Mapping order", order);
      const orderNumber = order?.order_number || String(order?.id || '');
      const date = this.formatDate(order?.date_created);
      const details = order?.details || {}; 
      const title = details.group?.name  || '-';
      const terms = details.group?.start_date + ' - ' + details.group?.end_date ;
      const participants = details.participants_count ? `Liczba uczestników: ${details.participants_count}` : '';
      const amount = this.formatAmount(order?.total_amount);
      const status = order?.status || 'new';
      const level = (details.group?.tags?.filter(tag => tag.type === 'path').map(tag => new Tag(tag))) || [];

      const invoiceUrl = this.resolveInvoiceUrl(order);
      const contractUrl = this.resolveContractUrl(order);

      return {
        id: order?.id,
        orderNumber,
        date,
        title,
        terms,
        participants,
        amount,
        status,
        level,
        invoiceUrl,
        contractUrl,
      };
    },

    resolveInvoiceUrl(order) {
      const invoiceUrl = order?.invoice_url || null;
      if (invoiceUrl) return this.resolveUrl(invoiceUrl);

      const invoiceFileId = order?.invoice || null;
      if (!invoiceFileId) return null;

      return this.resolveUrl(`assets/${invoiceFileId}?download`);
    },

    resolveUrl(url) {
      try {
        if (!url) return null;
        if (typeof url !== 'string') return null;
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return new URL(url.startsWith('/') ? url.slice(1) : url, rpAPI.apiBase).toString();
      } catch {
        return url;
      }
    },

    resolveContractUrl(order) {
      const contractFileId = order?.contract || null;
      console.log("Resolving contract URL for file ID", contractFileId);
      if (!contractFileId) return null;

      return rpAPI.getAssetUrl(contractFileId, '?download');
    },

    getStatusClass(status) {
      if (status === 'paid') {
        return 'bg-success-100 text-success-800';
      } else if (status === 'cancelled' || status === 'error') {
        return 'bg-danger-50 text-danger-850';
      }
      return 'bg-neutral-100 text-neutral-600';
    },

    getStatusLabel(status) {
      const labels = {
        paid: 'Opłacone',
        cancelled: 'Anulowane',
        pending_payment: 'Oczekujące',
        new: 'Nowe',
        confirmed: 'Potwierdzone',
        error: 'Błąd',
      };
      return labels[status] || status;
    },

    formatAmount(value) {
      const numberValue = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(numberValue)) return '0';
      return new Intl.NumberFormat('pl-PL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numberValue);
    },

    formatDate(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      const text = date.toLocaleDateString('pl-PL', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    getFilenameFromDisposition(disposition) {
      if (!disposition) return null;
      const match = /filename\*?=([^;]+)/i.exec(disposition);
      if (!match) return null;

      const raw = match[1].trim();
      if (raw.toLowerCase().startsWith("utf-8''")) {
        try {
          return decodeURIComponent(raw.slice(7).replace(/^"|"$/g, ''));
        } catch {
          return raw.slice(7).replace(/^"|"$/g, '');
        }
      }
      return raw.replace(/^"|"$/g, '');
    },

    
  },
};
</script>
